import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID, DOCUMENT } from '@angular/core';
import { environment } from '@env/environment';
import { activeGroupsInclude } from '@shared/helpers/one-trust-groups';
import { Subject } from 'rxjs';

interface OneTrustWindow extends Window {
  OnetrustActiveGroups?: string;
  OptanonWrapper?: () => void;
}

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly consentChanges = new Subject<void>();
  readonly consentChanges$ = this.consentChanges.asObservable();
  private installed = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone
  ) {
    this.install();
  }

  install(): void {
    if (!isPlatformBrowser(this.platformId) || this.installed) {
      return;
    }

    const win = this.document.defaultView as OneTrustWindow | null;
    if (!win) {
      return;
    }

    const previousWrapper = win.OptanonWrapper;
    win.OptanonWrapper = () => {
      if (typeof previousWrapper === 'function') {
        previousWrapper();
      }
      this.notifyConsentChanged();
    };

    const onGroupsUpdated = () => this.notifyConsentChanged();
    win.addEventListener('OneTrustGroupsUpdated', onGroupsUpdated);
    this.document.addEventListener('OneTrustGroupsUpdated', onGroupsUpdated);

    this.installed = true;
  }

  hasTargetingConsent(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const win = this.document.defaultView as OneTrustWindow | null;
    return activeGroupsInclude(
      win?.OnetrustActiveGroups,
      environment.cookieConsent.targetingGroupId
    );
  }

  private notifyConsentChanged(): void {
    this.ngZone.run(() => this.consentChanges.next());
  }
}
